// A copy of Harbor's rules for where a connector runs on AWS
// (packages/integrations/harbor/shared/src/aws-hosting.ts), kept apart so that
// LaunchDarkly changes no other integration.

/** An AWS account's ID. A copy of `AWS_ACCOUNT_PATTERN` in
 * `@p0security/integrations-aws-shared/constants`: this package doesn't depend
 * on the AWS integration's, and one pattern isn't worth the dependency. */
const AWS_ACCOUNT_PATTERN = /^\d{12}$/;

/** An AWS region, such as `us-west-2`. */
export const AWS_REGION_PATTERN = /^[a-z]+(?:-[a-z]+)+-\d+$/;

/** A region outside AWS's commercial partition: GovCloud, China, the ISO
 * regions (`us-iso-`, `us-isob-`, `eu-isoe-`, `us-isof-`) or the European
 * Sovereign Cloud. P0 invokes the connector, and points it at an account's
 * secret, by `arn:aws:` ARNs, so the connector can't be in one. */
export const AWS_OTHER_PARTITION_REGION_PATTERN =
  /^(?:us-gov-|cn-|[a-z]+-iso[a-z]*-|eusc-)/;

export const INVALID_AWS_ACCOUNT_ID =
  "Enter the 12-digit ID of the AWS account the connector runs in.";

export const INVALID_AWS_REGION =
  "Enter the AWS region the connector runs in, such as us-west-2.";

export const UNSUPPORTED_AWS_REGION =
  "P0's LaunchDarkly connector supports only AWS commercial regions for now.";

/** Whether `accountId`, ignoring surrounding whitespace, is an AWS account's
 * ID. */
export const isAwsAccountId = (accountId: unknown): accountId is string =>
  typeof accountId === "string" && AWS_ACCOUNT_PATTERN.test(accountId.trim());

/** What to tell the admin when a LaunchDarkly connector can't run in `region`, or
 * `undefined` when it can: {@link INVALID_AWS_REGION} for anything that isn't
 * a region's name, and {@link UNSUPPORTED_AWS_REGION} for a region outside
 * AWS's commercial partition. The value is checked trimmed, as P0 stores it.
 */
export const findAwsRegionProblem = (region: unknown) => {
  if (typeof region !== "string") return INVALID_AWS_REGION;
  const candidate = region.trim();
  if (!AWS_REGION_PATTERN.test(candidate)) return INVALID_AWS_REGION;
  return AWS_OTHER_PARTITION_REGION_PATTERN.test(candidate)
    ? UNSUPPORTED_AWS_REGION
    : undefined;
};
